<template>
  <v-container>
    <!-- 頂部操作列 -->
    <div class="d-flex align-center mb-6">
      <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" placeholder="輸入日期或科別或班級" outlined dense
        hide-details class="flex-grow-1 mr-4"></v-text-field>

      <v-btn color="black" dark @click="addTest">
        新增測驗
      </v-btn>
    </div>

    <!-- 測驗列表 -->
    <v-data-table :headers="headers" :items="filteredTests" hide-default-footer class="elevation-0" :items-per-page="-1">
      <!-- 自定義列顯示 -->
      <template v-slot:item="{ item }">
        <tr>
          <td class="text-start">{{ formatDateTime(item.startTime) }}</td>
          <td class="text-start">{{ formatDateTime(item.endTime) }}</td>
          <td class="text-start">{{ item.studentGroup.name }}</td>
          <td class="text-start">{{ item.name }}</td>
          <td class="text-right">
            <v-icon small class="mr-2" @click="viewResults(item)">
              mdi-eye
            </v-icon>
            <v-icon small class="mr-2" @click="editTest(item)">
              mdi-pencil
            </v-icon>
            <v-icon small @click="deleteTest(item)">
              mdi-delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- 新增/編輯測驗對話框 -->
    <v-dialog v-model="dialog" max-width="1000px">
      <v-card>
        <!-- 頂部進度指示和標題 -->
        <v-card-title class="d-flex align-center pa-4">
          <div class="d-flex align-center">
            <v-icon :color="currentStep === 1 ? 'black' : 'grey'" class="mr-2">
              mdi-circle
            </v-icon>
            <v-icon :color="currentStep === 2 ? 'black' : 'grey'" class="mr-2">
              mdi-circle
            </v-icon>
          </div>
          <span class="text-h6 ml-2">{{ currentStep === 1 ? '輸入測驗資料' : '選擇虛擬病患' }}</span>
        </v-card-title>

        <!-- 第一頁：測驗資料 -->
        <v-window v-model="currentStep">
          <v-window-item :value="1">
            <v-card-text class="pa-4">
              <v-form>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">測驗名稱</div>
                  <v-text-field v-model="formData.name" outlined dense hide-details placeholder="輸入測驗名稱"></v-text-field>
                </div>

                <div class="d-flex mb-4">
                  <div class="flex-grow-1 mr-4">
                    <div class="subtitle-1 font-weight-bold mb-2">開始</div>
                    <v-text-field v-model="formData.startTime" outlined dense hide-details type="datetime-local"
                      @input="handleDateTimeInput('startTime', $event)"></v-text-field>
                  </div>

                  <div class="flex-grow-1">
                    <div class="subtitle-1 font-weight-bold mb-2">結束</div>
                    <v-text-field v-model="formData.endTime" outlined dense hide-details type="datetime-local"
                      @input="handleDateTimeInput('endTime', $event)"></v-text-field>
                  </div>
                </div>

                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">群組</div>
                  <v-select v-model="formData.studentGroup" :items="studentGroups" item-text="name" item-value="_id"
                    outlined dense hide-details return-object></v-select>
                </div>
              </v-form>
            </v-card-text>
          </v-window-item>

          <!-- 第二頁：選擇虛擬病患 -->
          <v-window-item :value="2">
            <v-card-text class="pa-4">
              <!-- 科別選擇 -->
              <div v-for="patientCategory in patientCategorys" :key="patientCategory._id" class="mb-4 mr-4 ml-4">
                <div class="subtitle-1 text-left">
                  {{ patientCategory.topicName }}
                </div>
                <v-divider class="mb-4"></v-divider>


                <!-- 病患卡片列表 -->
                <v-row>
                  <v-col v-for="patient in patientCategory.patients" :key="patient._id" cols="12" sm="6" md="3">
                    <v-card class="patient-card" dark :class="{ 'selected': formData.patient === patient._id }"
                      @click="togglePatientSelection(patient)">
                      <div class="patient-card-background" :style="{
                        backgroundImage: `url(${returnImageUrl(patient.backgroundImage)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.5
                      }"></div>
                      <v-card-text class="patient-card-content">
                        <div class="d-flex justify-end">
                          <v-checkbox v-model="formData.patient" :value="patient._id" class="mt-0" hide-details
                            @click.stop></v-checkbox>
                        </div>
                        <div class="patient-info mt-auto">
                          <div class="text-h6">{{ patient.name }}</div>
                          <div>{{ patient.age }}歲,{{ patient.gender }}</div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-window-item>
        </v-window>

        <!-- 底部按鈕 -->
        <v-card-actions class="pa-4">
          <v-btn v-if="currentStep === 2" text @click="currentStep = 1">
            上一步
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="black" dark min-width="120" @click="currentStep === 1 ? nextStep() : saveTest()">
            {{ currentStep === 1 ? '下一步' : '儲存' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 刪除確認對話框 -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>確認刪除</v-card-title>
        <v-card-text>
          您確定要刪除這個測驗嗎？此操作無法復原。
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="deleteDialog = false">
            取消
          </v-btn>
          <v-btn color="red darken-1" text @click="confirmDelete">
            刪除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 在 v-dialog 區塊後新增成績顯示對話框 -->
    <v-dialog v-model="resultDialog" max-width="800px">
      <v-card>
        <v-card-title class="headline">
          學生成績
        </v-card-title>
        <v-card-text>
          <v-data-table :headers="resultHeaders" :items="testResults" hide-default-footer
            class="elevation-0 result-table">
            <template v-slot:item="{ item }">
              <tr>
                <td class="text-left">{{ formatOtherDateTime(item.uploadTime) }}</td>
                <td class="text-left">{{ item.student ? item.student.name : "無法取得學生姓名" }}</td>
                <td class="text-left">{{ item.examScore }}</td>
                <td class="text-left">
                  <v-btn small text color="primary" @click="showAnswerDetail(item)">
                    <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                  </v-btn>
                </td>
                <td class="text-left">
                  <v-btn small text color="primary" @click="viewScoreSheet(item)">
                    <v-icon small>mdi-file-check-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
          <div class="d-flex justify-end mt-4">
            <v-btn color="primary" text @click="exportToCsv">
              輸出 CSV
            </v-btn>
            <v-btn color="primary" text @click="resultDialog = false">
              關閉
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 新增答題過程查看對話框 -->
    <exam-answer-viewer v-model="showAnswerViewer" :exam-result="selectedExamResult" :student-answer="studentAnswer" />

    <v-dialog v-model="showScoreSheet" width="1000">
      <editable-score-sheet 
        :exam-data="examData" 
        @save="handleSave"
        @export-csv="handleExportCSV" 
        @close="handleClose" 
      />
    </v-dialog>
  </v-container>
</template>

<script>
import PatientService from '@/services/patient.service';
import StudentGroupService from '@/services/studentGroup.service';
import TestService from '@/services/test.service';
import baseUrl from '../../../baseUrl.config';
import ExamResultService from '@/services/examResult.service';
import ExamAnswerViewer from '@/components/ExamAnswerViewer.vue';
import EditableScoreSheet from '@/components/EditableScoreSheet.vue'

export default {
  name: 'TeacherTests',
  components: {
    ExamAnswerViewer,
    EditableScoreSheet
  },
  data() {
    return {
      search: '',
      dialog: false,
      deleteDialog: false,
      startMenu: false,
      endMenu: false,
      isEditing: false,
      selectedTest: null,
      headers: [
        { text: '開始', value: 'startTime', width: '20%', align: 'start' },
        { text: '結束', value: 'endTime', width: '20%', align: 'start' },
        { text: '群組', value: 'department', width: '15%', align: 'start' },
        { text: '測驗名稱', value: 'name', width: '35%', align: 'start' },
        { text: '操作', value: 'actions', sortable: false, width: '10%', align: 'end' }
      ],
      tests: [
      ],
      departments: ['牙醫系', '醫學系', '護理系'],
      formData: {
        name: '',
        startTime: '',
        endTime: '',
        studentGroup: '',
        patient: '',
      },
      currentStep: 1,

      patientCategorys: [{ name: '牙醫科', patients: [] }],
      studentGroups: [],
      resultDialog: false,
      resultHeaders: [
        { text: '完成時間', value: 'addDatetime', width: '25%' },
        { text: '姓名', value: 'studentName', width: '25%' },
        { text: '總分', value: 'examScore', width: '25%' },
        { text: '答題過程', value: 'answer', width: '10%', sortable: false },
        { text: '評分表', value: 'scoreSheet', width: '10%', sortable: false },
      ],
      testResults: [],
      showAnswerViewer: false,
      selectedExamResult: {
        // 模擬資料結構
        name: "",
        gender: "",
        age: "",
        description: "",
        backgroundImage: "",
        clinicFiles: [],
        firstVisitFiles: [],
        postOpFiles: [],
        firstVisitQuestionExist: true,
        postOpQuestionExist: true,
        student_id: "",
        messages: [
        ],
        firstVisitQuestions: [
        ],
        postOpQuestions: [
        ]
      },
      studentAnswer: {
        // 模擬資料結構
      },
      showScoreSheet: false,
      examData: {
        exam: {
          name: '',
          duration: 0
        },
        student: {
          name: ''
        },
        examResult: {
          result: 0,
          comment: ''
        },
        teacher: {
          name: ''
        },
        examResultDetail: []
      },

    }
  },
  async mounted() {
    await this.getPatientSortByTagCategoryTopic()
    await this.getStudentGroupBySchool()
    await this.getTestByTeacher()
  },
  methods: {
    async getPatientSortByTagCategoryTopic() {
      const res = await PatientService.getPatientSortByTagCategoryTopic()
      this.patientCategorys = res.data.data
    },
    async getStudentGroupBySchool() {
      const res = await StudentGroupService.getGroupByTeacher()

      this.studentGroups = res.data.filter(item => item.status === true)
    },
    async getTestByTeacher() {
      const res = await TestService.getByTeacher()
      this.tests = res.data.tests
      console.log(this.tests)
    },
    addTest() {
      this.isEditing = false
      this.resetForm()
      this.dialog = true
    },
    editTest(test) {
      this.isEditing = true
      this.selectedTest = test

      // 處理日期
      const startDate = new Date(test.startTime);
      const endDate = new Date(test.endTime);

      this.formData = {
        name: test.name,
        startTime: this.formatDateForInput(startDate),
        endTime: this.formatDateForInput(endDate),
        studentGroup: test.studentGroup,
        patient: test.patient
      };
      this.dialog = true
    },
    deleteTest(test) {
      this.selectedTest = test
      this.deleteDialog = true
    },
    async viewResults(test) {
      this.selectedTest = test;
      const response = await ExamResultService.teacherFindExamResultByTestId(test._id);
      this.testResults = response.data.data;

      // // 暫時使用模擬數據
      //   this.testResults = [
      //   {
      //     completedTime: new Date(),
      //     studentName: '林凱強',
      //     score: 95,
      //   }
      // ];

      this.resultDialog = true;
    },
    saveTest() {
      if (!this.formData.patient) {
        alert('請選擇一個虛擬病患');
        return;
      }
      if (!this.formData.name) {
        alert('請輸入測驗名稱');
        return;
      }
      if (!this.formData.studentGroup) {
        alert('請選擇一個群組');
        return;
      }
      if (!this.formData.startTime) {
        alert('請選擇開始時間');
        return;
      }
      if (!this.formData.endTime) {
        alert('請選擇結束時間');
        return;
      }

      // 直接使用 datetime-local 的值來創建日期對象
      const startDate = new Date(this.formData.startTime);
      const endDate = new Date(this.formData.endTime);

      const testData = {
        startTime: startDate,  // 直接傳送 Date 對象
        endTime: endDate,      // 直接傳送 Date 對象
        studentGroup: this.formData.studentGroup,
        name: this.formData.name,
        patient: this.formData.patient
      };



      if (this.isEditing && this.selectedTest) {
        testData._id = this.selectedTest._id
        TestService.update(testData).then(() => {
          this.getTestByTeacher();
        });
      } else {
        TestService.add(testData).then(() => {
          this.getTestByTeacher();
        });
      }
      this.dialog = false;
      this.resetForm();
    },
    confirmDelete() {
      TestService.delete(this.selectedTest._id).then(() => {
        this.getTestByTeacher();
      });
      this.deleteDialog = false
      this.selectedTest = null
    },
    resetForm() {
      this.formData = {
        name: '',
        startTime: '',
        endTime: '',
        studentGroup: '',
        patient: ''
      };

      this.selectedTest = null;
      this.currentStep = 1;
    },
    togglePatientSelection(patient) {
      this.formData.patient = this.formData.patient === patient._id ? '' : patient._id;
    },
    nextStep() {
      if (this.currentStep < 2) {
        this.currentStep++
      }
    },
    returnImageUrl(imageUrl) {
      return baseUrl + "uploadFiles/backgroundImage/" + imageUrl
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '';

      // 直接處理 ISO 字符串
      const date = new Date(timestamp);

      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(/\//g, '-');
    },
    formatOtherDateTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    },
    handleDateTimeInput(field, value) {
      if (value) {
        // 直接存儲 datetime-local 的值
        this.formData[field] = value;
      }
    },
    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    showAnswerDetail(result) {
      ExamResultService.teacherGetExamResultDetail(result._id).then((res) => {
        this.selectedExamResult = res.data.data.patient;
        this.studentAnswer = { messages: res.data.data.exam_result_id.messages, answers: res.data.data.exam_result_id.answerData[0] };
        this.showAnswerViewer = true;

      });
    },
    viewScoreSheet(result) {
      ExamResultService.teacherGetExamResultDetail(result._id).then((res) => {
        console.log(res.data.data)
        this.examData = res.data.data;
        this.selectedTest = result;  // 保存當前選中的測驗
        this.showScoreSheet = true;
      });
    },
    async handleSave(updatedData) {
      try {
        await ExamResultService.updateExamResult(updatedData._id, updatedData);
        this.showScoreSheet = false;
      } catch (error) {
        console.error('儲存失敗:', error);
      }
    },
    handleClose() {
      this.showScoreSheet = false;
    },
    handleExportCSV() {
      // 實作匯出 CSV 的邏輯
      console.log('Export to CSV');
    },
    exportToCsv() {
      // 準備 CSV 內容
      const headers = [
        '完成時間',
        '學生姓名',
        '總分',
        '測驗項目',
        '評語',
        '評分考官'
      ];
      
      let csvContent = headers.join(',') + '\n';
      
      // 添加每個學生的成績資料
      this.testResults.forEach(result => {
        const row = [
          this.formatOtherDateTime(result.uploadTime),
          result.student?.name || '',
          result.examScore || 0,
          this.selectedTest?.name || '',
          result.examResult?.comment || '',
          result.teacher?.name || '考官'
        ].map(field => `"${field}"`).join(',');
        
        csvContent += row + '\n';
      });

      // 添加 BOM 以確保 Excel 正確顯示中文
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `測驗成績_${this.selectedTest?.name || '未命名'}_${new Date().toLocaleDateString()}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  },
  computed: {
    filteredTests() {
      if (!this.search) {
        return this.tests;
      }

      const searchTerm = this.search.toLowerCase().trim();

      return this.tests.filter(test => {
        // 搜尋日期（轉換成 YYYY-MM-DD 格式進行比對）
        const startDate = this.formatDateTime(test.startTime);
        const endDate = this.formatDateTime(test.endTime);
        const dateMatch = startDate.includes(searchTerm) ||
          endDate.includes(searchTerm);

        // 搜尋群組名稱
        const groupMatch = test.studentGroup?.name?.toLowerCase().includes(searchTerm);

        // 搜尋測驗名稱
        const nameMatch = test.name?.toLowerCase().includes(searchTerm);

        return dateMatch || groupMatch || nameMatch;
      });
    }
  }
}
</script>

<style scoped>
.v-data-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  /* 加上淺灰色邊框 */
}

.v-data-table ::v-deep td {
  padding: 8px 16px !important;
}

.v-data-table ::v-deep th {
  font-weight: bold !important;
  white-space: nowrap;
}

.patient-card {
  height: 300px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.patient-card-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.patient-card-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.patient-info {
  margin-top: auto;
}

.patient-card.selected {
  border: 2px solid white;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
}

.result-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.result-table ::v-deep td {
  padding: 8px 16px !important;
}

.result-table ::v-deep th {
  font-weight: bold !important;
  white-space: nowrap;
}
</style>
